import { useCallback, useEffect, useMemo, useState } from 'react'

import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { CustomEvent } from '../../../enums/customEvent'
import { LocalStorageKeys } from '../../../enums/localStorageKeys'
import { useCustomEventListener } from '../../../hooks/useCustomEventListener'
import type { ReviewEntryItem } from '../types/reviewEntryItem'
import type { ReviewEntryTableRow } from '../types/reviewEntryTableRow'
import { applyReviewEntryDraftsToItems } from '../utils/applyReviewEntryDraftToItem'
import { flattenReviewEntriesToTableRows } from '../utils/flattenReviewEntriesToTableRows'
import { pruneExpiredReviewEntryDrafts } from '../utils/reviewEntryDraftStorage'
import { useFetchReviewEntries } from './useFetchReviewEntries'
import { useReviewEntriesFeature } from './useReviewEntriesFeature'

interface UseReviewEntriesTableDataProps {
  beginDate: string
  endDate: string
}

interface UseReviewEntriesTableDataResult {
  isError: boolean
  isLoading: boolean
  items: ReviewEntryItem[]
  itemsWithDrafts: ReviewEntryItem[]
  pagination?: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
  rows: ReviewEntryTableRow[]
}

export const useReviewEntriesTableData = ({
  beginDate,
  endDate,
}: UseReviewEntriesTableDataProps): UseReviewEntriesTableDataResult => {
  const { isEnabled } = useReviewEntriesFeature()
  const { accounts } = useFetchAccounts()
  const { organization } = useUserOrganization()
  const organizationId = organization?.id
  const canFetchEntries = isEnabled
  const { isError, isLoading, reviewEntries } = useFetchReviewEntries({
    beginDate,
    enabled: canFetchEntries,
    endDate,
  })
  const [draftOverlayVersion, setDraftOverlayVersion] = useState(0)
  const handleDraftsChanged = useCallback(() => {
    setDraftOverlayVersion((current) => current + 1)
  }, [])

  const items = useMemo(() => {
    if (!canFetchEntries || isError || !reviewEntries?.items?.length) {
      return []
    }

    return reviewEntries.items
  }, [canFetchEntries, reviewEntries?.items, isError])

  // Overlay local drafts onto table rows and bulk-validate gating. `items` stay as returned
  // by the API so the detail form (which already restores from localStorage) is not remounted
  // from overlay rows. Same-tab saves bump `draftOverlayVersion` so this recomputes.
  const itemsWithDrafts = useMemo(
    () => applyReviewEntryDraftsToItems(items, organizationId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overlay reads localStorage, not `items`
    [draftOverlayVersion, items, organizationId],
  )

  const rows = useMemo(() => flattenReviewEntriesToTableRows(itemsWithDrafts, accounts), [accounts, itemsWithDrafts])

  const pagination = useMemo(() => {
    if (!canFetchEntries || isError || !reviewEntries) {
      return undefined
    }

    return {
      page: reviewEntries.page,
      pageSize: reviewEntries.pageSize,
      total: reviewEntries.total,
      totalPages: reviewEntries.totalPages,
    }
  }, [canFetchEntries, reviewEntries, isError])

  useCustomEventListener(CustomEvent.ReviewEntryDraftChanged, handleDraftsChanged)

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (!event.key?.startsWith(`${LocalStorageKeys.ReviewEntryDraft}-`)) {
        return
      }

      handleDraftsChanged()
    }

    globalThis.addEventListener('storage', handleStorage)

    return () => {
      globalThis.removeEventListener('storage', handleStorage)
    }
  }, [handleDraftsChanged])

  useEffect(() => {
    pruneExpiredReviewEntryDrafts()
  }, [])

  return {
    isError,
    isLoading,
    items,
    itemsWithDrafts,
    pagination,
    rows,
  }
}
